import * as globals from '@wandb/weave/common/css/globals.styles';
import {Button} from '@wandb/weave/components/Button';
import {IconSearch} from '@wandb/weave/components/Icon';
import classNames from 'classnames';
import {debounce} from 'lodash';
import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import TextareaAutosize from 'react-textarea-autosize';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Popup} from 'semantic-ui-react';
// eslint-disable-next-line wandb/no-deprecated-imports
import styled from 'styled-components';

import {SearchHistory} from './PanelBank/SearchHistory';
import {trackSearchHistory} from './PanelBank/searchHistoryEvents';
import {OptionTypeWithRegex, RunSearchHistoryItem} from './PanelBank/types';

interface RunsSearchBarProps {
  isGroupSearch: boolean;
  isInReport?: boolean;
  search: {
    query: string;
    isRegex: boolean;
  };
  setSearch(search: string, isRegex: boolean): void;
  searchHistory?: RunSearchHistoryItem[];
  onAddToHistory?(query: string, regex?: boolean): void;
}

const HEIGHT_TRANSITION_DURATION_MS = 100;

const SearchInputContainer = styled.div`
  display: flex;
`;
SearchInputContainer.displayName = 'S.SearchInputContainer';

const SearchInput = styled(TextareaAutosize)<{
  $shouldTransitionHeight: boolean;
}>`
  width: 100%;
  min-width: 200px;
  resize: none;
  line-height: 24px;
  padding: 4px 32px;
  border-radius: 4px;
  border: 1px solid ${globals.MOON_250};
  transition: ${p =>
    p.$shouldTransitionHeight
      ? `height ${HEIGHT_TRANSITION_DURATION_MS}ms`
      : 'none'};
  word-break: break-all;
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none;
  }
  &::placeholder {
    color: ${globals.MOON_500};
  }
  &:focus,
  &:focus-visible {
    border: 1px solid ${globals.TEAL_400};
    box-shadow: 0 0 0 1px ${globals.TEAL_400};
    outline: none;
  }
`;
SearchInput.displayName = 'S.SearchInput';

const SearchInputIcon = styled(IconSearch)<{
  $hasValue: boolean;
}>`
  height: 18px;
  width: 18px;
  color: ${props => (props.$hasValue ? globals.MOON_800 : globals.MOON_500)};
  position: absolute;
  top: 8px;
  left: 8px;
`;
SearchInputIcon.displayName = 'S.SearchInputIcon';

const RunsSearchBarComp: React.FC<RunsSearchBarProps> = ({
  isGroupSearch,
  isInReport,
  search: propsSearch,
  setSearch,
  searchHistory,
  onAddToHistory,
}) => {
  const [inputValue, setInputValue] = useState(propsSearch.query);
  const [regex, setRegex] = useState(propsSearch.isRegex);

  const [selectedOption, setSelectedOption] =
    useState<OptionTypeWithRegex | null>(null);

  /**
   * Used for analytics tracking. We want to log "Runs searched" event
   * on blur, but only if the value has actually changed since focused.
   */
  const [valueAtFocus, setValueAtFocus] = useState('');

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        if (value !== propsSearch.query || regex !== propsSearch.isRegex) {
          setSearch(value, regex);
        }
      }, 500),
    [propsSearch, regex, setSearch]
  );

  useEffect(() => {
    const input = inputValue.trim();
    debouncedSearch(input);
  }, [inputValue, debouncedSearch, propsSearch, regex]);

  const toggleRegex = useCallback(
    event => {
      setRegex(prev => {
        const next = !prev;
        trackSearchHistory({
          action: 'Regex toggled',
          location: isInReport
            ? 'report run selector'
            : 'workspace run selector',
          regex: next ? 'on' : 'off',
        });
        return next;
      });
      event.stopPropagation();
    },
    [isInReport]
  );

  const onFocus = () => {
    if (valueAtFocus !== inputValue) {
      setValueAtFocus(inputValue);
    }
  };

  const onBlur = (inputValue: string) => {
    if (inputValue !== valueAtFocus) {
      trackSearchHistory({
        action: 'Item added',
        location: isInReport ? 'report run selector' : 'workspace run selector',
        regex: regex ? 'on' : 'off',
      });
      setValueAtFocus('');
    }
    // when to add to search history is a bit tricky - we chose blur since
    // it is a reasonable proxy for "the user finished editing the search",
    // and trying to save after X seconds of pause could be wrong if a
    // user stopped to think of something while typing a search.
    if (!inputValue) {
      return;
    }
    onAddToHistory?.(inputValue, regex);
    trackSearchHistory({
      action: 'Item selected',
      location: isInReport ? 'report run selector' : 'workspace run selector',
      regex: regex ? 'on' : 'off',
    });
  };

  const onOptionChange = (newValue: OptionTypeWithRegex | string) => {
    const newOption =
      typeof newValue === 'string'
        ? {
            value: newValue,
            label: newValue,
            isRegex: regex,
          }
        : newValue;
    setSelectedOption(newOption);
    setRegex(newOption.isRegex);
  };

  return (
    <SearchHistory
      searchHistory={searchHistory ?? []}
      onOptionChange={onOptionChange}
      isCollapsible
      selectedOption={selectedOption}
      setSelectedOption={setSelectedOption}
      onFocus={onFocus}
      onBlur={onBlur}
      minWidth={isInReport ? 200 : 150}
      setInputValue={setInputValue}
      inputValue={inputValue ?? ''}
      placeholderText={`Search ${isGroupSearch ? 'groups' : 'runs'}`}
      maxItems={10}
      customActionButton={
        <Popup
          className="runs-search-bar-regex-toggle-popup"
          content={`Regex matching is ${regex ? 'on' : 'off'}`}
          position="top center"
          popperModifiers={{
            preventOverflow: {
              boundariesElement: 'viewport',
            },
          }}
          inverted
          trigger={
            <Button
              aria-label="Toggle regex matching"
              size="small"
              variant="ghost"
              icon="regex"
              className={classNames('dark:bg-moon-950', {
                // custom hex values are because this button needs to have opaque
                // bg color, but the base component uses colors with opacity
                //   - #f5f5f5 = oblivion/5%
                //   - #d7f6f9 = teal-300/48%
                //   - #163942 = teal-700/40%
                'text-moon-500 hover:bg-[#f5f5f5] hover:text-moon-800': !regex,
                'dark:hover:bg-moon-750 dark:hover:text-moon-100': !regex,
                'bg-[#d7f6f9] dark:bg-[#163942]': regex,
              })}
              onClick={toggleRegex}
              active={regex}
            />
          }
        />
      }
    />
  );
};

export const RunsSearchBar = memo(RunsSearchBarComp);
